import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams, useLocation, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Mail, Lock, User, Phone, Eye, EyeOff, ArrowRight, BadgeCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CustomerAuth = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get('invite');
  const initialMode = inviteToken || location.pathname === '/register' ? 'register' : 'login';
  const { login, register, login: loginCtx } = useAuth();
  const [mode, setMode] = useState(initialMode);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [inviteInfo, setInviteInfo] = useState(null);  // { email, name, phone } when invite is valid
  const [inviteError, setInviteError] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    newsletter_subscribed: true,
  });

  // Resolve the invite token (if any) on mount and pre-fill the form.
  useEffect(() => {
    if (!inviteToken) return;
    axios.get(`${API}/invite/${inviteToken}`)
      .then((res) => {
        setInviteInfo(res.data);
        setFormData((d) => ({
          ...d,
          name: res.data.name || '',
          email: res.data.email || '',
          phone: res.data.phone || '',
        }));
      })
      .catch((err) => {
        const code = err?.response?.status;
        const detail = err?.response?.data?.detail;
        setInviteError(
          code === 410 ? (detail || 'This invite has already been used or expired. Please log in or register normally.')
          : code === 404 ? 'This invite link is invalid. Please log in or register normally.'
          : 'Could not validate the invite right now.'
        );
      });
  }, [inviteToken]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(formData.email, formData.password);
      toast.success('Welcome back!');
      navigate('/account');
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.response?.data?.detail || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setLoading(true);
    try {
      if (inviteToken && inviteInfo) {
        // Finish registration through the single-use invite flow.
        const res = await axios.post(`${API}/customer/register-from-invite`, {
          token: inviteToken,
          password: formData.password,
          newsletter_subscribed: formData.newsletter_subscribed,
        });
        // Auth context expects to manage the token itself — push the new
        // credentials in via login() so the user lands logged-in.
        const { access_token, user } = res.data;
        localStorage.setItem('token', access_token);
        // Hydrate auth context the simple way: log in with email+password (we just set it).
        try { await loginCtx(user.email, formData.password); }
        catch { window.location.href = '/account/profile'; return; }
        toast.success('Account created — welcome!');
        navigate('/account/profile');
        return;
      }
      await register({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        password: formData.password,
        newsletter_subscribed: formData.newsletter_subscribed,
      });
      
      toast.success('Account created successfully!');
      navigate('/account/profile');
    } catch (error) {
      console.error('Registration error:', error);
      const detail = error.response?.data?.detail;
      if (detail === 'Email already registered' || error.response?.status === 409) {
        toast.error(detail || 'An account already exists for this email — please log in.');
        setMode('login');
      } else if (error.response?.status === 410) {
        toast.error(detail || 'This invite has already been used or expired.');
      } else {
        toast.error(detail || 'Failed to create account');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="py-16" data-testid="customer-auth-page">
      <div className="max-w-md mx-auto px-4 sm:px-6 lg:px-8">
        {inviteToken && !inviteError && inviteInfo && (
          <div className="mb-6 flex items-start gap-3 p-4 bg-emerald-500/10 border-2 border-emerald-500/30 rounded-sm" data-testid="invite-banner">
            <BadgeCheck size={20} className="text-emerald-700 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-emerald-900">
              <strong>Welcome, {inviteInfo.name?.split(' ')[0] || 'traveler'}!</strong>
              <br />
              <span className="text-emerald-800/80">We've pre-filled your details from your recent quote — just pick a password to finish.</span>
            </div>
          </div>
        )}
        {inviteToken && inviteError && (
          <div className="mb-6 p-4 bg-coral/10 border-2 border-coral/30 rounded-sm text-sm text-coral" data-testid="invite-error-banner">
            {inviteError}
          </div>
        )}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-heading font-bold text-maroon mb-4">
            {mode === 'login' ? 'Welcome Back' : 'Create Account'}
          </h1>
          <p className="text-maroon/70">
            {mode === 'login' 
              ? 'Sign in to manage your bookings and profile' 
              : 'Join Chimba Travel and start your adventure'}
          </p>
        </div>

        {/* Mode Toggle */}
        <div className="flex mb-8 bg-sand/30 rounded-sm p-1">
          <button
            type="button"
            onClick={() => setMode('login')}
            className={`flex-1 py-3 px-4 rounded-sm font-semibold transition-colors ${
              mode === 'login' 
                ? 'bg-maroon text-white' 
                : 'text-maroon hover:bg-sand/50'
            }`}
            data-testid="login-tab"
          >
            Sign In
          </button>
          <button
            type="button"
            onClick={() => setMode('register')}
            className={`flex-1 py-3 px-4 rounded-sm font-semibold transition-colors ${
              mode === 'register' 
                ? 'bg-maroon text-white' 
                : 'text-maroon hover:bg-sand/50'
            }`}
            data-testid="register-tab"
          >
            Register
          </button>
        </div>

        <div className="card">
          {mode === 'login' ? (
            <form onSubmit={handleLogin} className="space-y-6" data-testid="login-form">
              <div className="space-y-2">
                <Label htmlFor="login-email" className="flex items-center gap-2">
                  <Mail size={16} />
                  Email
                </Label>
                <Input
                  id="login-email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="input-field"
                  placeholder="your@email.com"
                  data-testid="login-email-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="login-password" className="flex items-center gap-2">
                  <Lock size={16} />
                  Password
                </Label>
                <div className="relative">
                  <Input
                    id="login-password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={handleChange}
                    required
                    className="input-field pr-10"
                    placeholder="••••••••"
                    data-testid="login-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-maroon/60 hover:text-maroon"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <Button 
                type="submit" 
                className="btn-primary w-full"
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </Button>
            </form>
          ) : (
            <form onSubmit={handleRegister} className="space-y-6" data-testid="register-form">
              <div className="space-y-2">
                <Label htmlFor="reg-name" className="flex items-center gap-2">
                  <User size={16} />
                  Full Name
                </Label>
                <Input
                  id="reg-name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="input-field"
                  placeholder="John Smith"
                  data-testid="register-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="reg-email" className="flex items-center gap-2">
                  <Mail size={16} />
                  Email
                </Label>
                <Input
                  id="reg-email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="input-field"
                  placeholder="your@email.com"
                  data-testid="register-email-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="reg-phone" className="flex items-center gap-2">
                  <Phone size={16} />
                  Phone (optional)
                </Label>
                <Input
                  id="reg-phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  className="input-field"
                  placeholder="+1 (555) 123-4567"
                  data-testid="register-phone-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="reg-password" className="flex items-center gap-2">
                  <Lock size={16} />
                  Password
                </Label>
                <div className="relative">
                  <Input
                    id="reg-password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={handleChange}
                    required
                    minLength={6}
                    className="input-field pr-10"
                    placeholder="Min. 6 characters"
                    data-testid="register-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-maroon/60 hover:text-maroon"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="reg-confirm" className="flex items-center gap-2">
                  <Lock size={16} />
                  Confirm Password
                </Label>
                <Input
                  id="reg-confirm"
                  name="confirmPassword"
                  type="password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                  className="input-field"
                  placeholder="••••••••"
                  data-testid="register-confirm-input"
                />
              </div>

              <label className="flex items-start gap-2 cursor-pointer p-3 border-2 border-maroon/15 rounded-sm bg-sand/30">
                <input
                  type="checkbox"
                  checked={formData.newsletter_subscribed}
                  onChange={(e) => setFormData({ ...formData, newsletter_subscribed: e.target.checked })}
                  className="mt-1"
                  data-testid="register-newsletter-checkbox"
                />
                <span className="text-sm text-maroon">
                  <strong>Subscribe to our monthly newsletter</strong>
                  <br />
                  <span className="text-maroon/60 text-xs">Travel inspiration & exclusive offers — once a month, no spam.</span>
                </span>
              </label>

              <p className="text-xs text-maroon/60">
                By creating an account, you agree to our{' '}
                <Link to="/terms" className="underline hover:text-maroon">Terms & Conditions</Link>.
              </p>

              <Button 
                type="submit" 
                className="btn-primary w-full"
                disabled={loading || (!!inviteToken && !inviteInfo && !inviteError)}
                data-testid="register-submit-btn"
              >
                {loading ? 'Creating Account...' : 'Create Account'}
              </Button>
            </form>
          )}
        </div>

        <div className="mt-8 text-center">
          <Link to="/packages" className="text-maroon hover:text-terracotta inline-flex items-center gap-2">
            <span>Browse Travel Packages</span>
            <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default CustomerAuth;
